import { ReactElement, useCallback } from 'react';
import { Stack, Tooltip } from '@mui/material';
import { faMagnifyingGlass as SearchIcon } from '@fortawesome/free-solid-svg-icons';

import { ORDERS_GLOBALS_OPTIONS } from '~constants/ordersStatesOptions';

import { useDialogOrderSearchContext } from './Context';
import DialogOrderSearchFiltersMenu from './FiltersMenu';
import { DEFAULT_PAGINATION, DEFAULT_FILTERS_STATE } from './utils';
import {
  DialogOrderSearchFiltersTextFieldWithButtonGroup,
  DialogOrderSearchFiltersTextFieldLarge,
  DialogOrderSearchFiltersIconButton,
  DialogOrderSearchFiltersButtonIcon,
} from './styles';

const DialogOrderSearchFilters = (): ReactElement => {
  const {
    selectsData,
    filters: {
      filters,
      handleChangeInputFilter,
      parseFiltersRequest,
      clearFilters,
      setIsFilterApplied,
      filtersActives,
      setFiltersActives,
      clearFiltersActives,
      handleChangeFilters,
      setFilterByKey,
    },
    table: { handleChangeTable, handleInitialTable },
  } = useDialogOrderSearchContext();

  const handleApplyFilter = useCallback(() => {
    handleChangeTable('filter', {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest(),
    });

    setFiltersActives(filters);

    setIsFilterApplied(true);
  }, [
    filters,
    handleChangeTable,
    parseFiltersRequest,
    setFiltersActives,
    setIsFilterApplied,
  ]);

  const handleResetFilters = useCallback(() => {
    handleChangeFilters?.(filtersActives);

    selectsData.drivers.autocompleteRef.current?.setValue(
      filtersActives.drivers ?? [],
    );

    selectsData.vehicles.autocompleteRef.current?.setValue(
      filtersActives.vehicles ?? [],
    );

    selectsData.services.autocompleteRef.current?.setValue(
      filtersActives.services ?? [],
    );

    selectsData.logisticsZones.autocompleteRef.current?.setValue(
      filtersActives.zones ?? [],
    );

    selectsData.users.autocompleteRef.current?.setValue(
      filtersActives.assignedUser ?? null,
    );

    const currentStates = ORDERS_GLOBALS_OPTIONS.filter((state) =>
      filtersActives.states.includes(state.value as string),
    );

    selectsData.states.autocompleteRef.current?.setValue(currentStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersActives, filters, handleChangeFilters]);

  const handleClearFilters = useCallback(() => {
    clearFilters(DEFAULT_FILTERS_STATE);
    clearFiltersActives();

    selectsData.drivers.autocompleteRef.current?.resetAutocomplete();
    selectsData.vehicles.autocompleteRef.current?.resetAutocomplete();
    selectsData.services.autocompleteRef.current?.resetAutocomplete();
    selectsData.logisticsZones.autocompleteRef.current?.resetAutocomplete();
    selectsData.states.autocompleteRef.current?.resetAutocomplete();
    selectsData.users.autocompleteRef.current?.resetAutocomplete();

    handleInitialTable();
    setIsFilterApplied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearFilters,
    clearFiltersActives,
    handleInitialTable,
    setIsFilterApplied,
  ]);

  return (
    <Stack direction="row" spacing={1}>
      <DialogOrderSearchFiltersTextFieldWithButtonGroup>
        <DialogOrderSearchFiltersTextFieldLarge
          label="Nro. Orden / Nro. Tracking / Grupo"
          name="search"
          value={filters.search ?? ''}
          onChange={handleChangeInputFilter}
        />

        <Tooltip title="Buscar..." arrow>
          <span style={{ display: 'flex' }}>
            <DialogOrderSearchFiltersIconButton
              color="secondary"
              variant="contained"
              onClick={handleApplyFilter}
              disabled={!filters.search}
            >
              <DialogOrderSearchFiltersButtonIcon icon={SearchIcon} />
            </DialogOrderSearchFiltersIconButton>
          </span>
        </Tooltip>
      </DialogOrderSearchFiltersTextFieldWithButtonGroup>

      <DialogOrderSearchFiltersMenu
        filters={filters}
        activeFilters={filtersActives}
        onChangeFilterInput={setFilterByKey}
        onResetFilters={handleResetFilters}
        onApplyFilters={handleApplyFilter}
        onClearFilters={handleClearFilters}
        selectsData={selectsData}
      />
    </Stack>
  );
};

export default DialogOrderSearchFilters;
